import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { BED_CLASS } from '@his/shared';
import { BedDouble } from 'lucide-react';
import { get, post, put } from '@/lib/api';
import { useBranchLock } from '@/lib/branch';
import { describeError, useAuth } from '@/lib/auth';
import { humanize } from '@/lib/utils';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { RecordState } from '@/components/ui/record-state';
import { Alert, Field, Input, Select } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { safeReturnTo } from '../opd/BookAppointmentPage';
import { orgReturnTo } from './org-types';
import { codeEntry, useBranchOptions, useRefreshWards, useWardRecord, type BranchOption, type DepartmentOption, type WardRow } from './ward-types';

export function WardPage() {
  const { wardId = '' } = useParams();
  const [params] = useSearchParams();
  const returnTo = safeReturnTo(params.get('returnTo'), orgReturnTo('wards'));
  const branchId = params.get('branchId') ?? '';

  const branches = useBranchOptions();
  const ward = useWardRecord(wardId, branchId);

  if (!branches.data || (wardId && !ward.data)) {
    return (
      <FormScreen title={wardId ? tr('admin', 'loading') : tr('admin', 'add_a_ward')} icon={BedDouble} backTo={returnTo}>
        <RecordState error={branches.error ?? ward.error} missing={ward.data === null} />
      </FormScreen>
    );
  }

  return <WardForm key={wardId || 'new'} ward={ward.data ?? null} branches={branches.data} defaultBranchId={branchId} returnTo={returnTo} />;
}

function WardForm({
  ward,
  branches,
  defaultBranchId,
  returnTo,
}: {
  ward: WardRow | null;
  branches: BranchOption[];
  defaultBranchId: string;
  returnTo: string;
}) {
  const navigate = useNavigate();
  const toast = useToast();
  const refresh = useRefreshWards();
  const { can } = useAuth();
  const lock = useBranchLock();
  const [initial] = useState(() => ({
    branchId: ward?.branchId || (lock.locked ? lock.branchId : defaultBranchId || (branches.length === 1 ? branches[0].id : '')),
    code: ward?.code ?? '',
    name: ward?.name ?? '',
    departmentId: ward?.departmentId ?? '',
    defaultBedClass: ward?.defaultBedClass ?? 'general',
    nurseStationPhone: ward?.nurseStationPhone ?? '',
  }));
  const [form, setForm] = useState(initial);
  const [error, setError] = useState<string | null>(null);
  const departmentsReadable = can('department.read');

  const departments = useQuery({
    queryKey: ['departments'],
    queryFn: () => get<DepartmentOption[]>('/org/departments'),
    enabled: !ward && departmentsReadable,
  });
  const departmentsForBranch = (departments.data ?? []).filter((department) => department.branchId === form.branchId);

  const save = useMutation({
    mutationFn: () =>
      ward
        ? put(`/ipd/wards/${ward.id}`, {
            name: form.name.trim(),
            defaultBedClass: form.defaultBedClass,
            nurseStationPhone: form.nurseStationPhone.trim() || null,
          })
        : post('/ipd/wards', {
            branchId: form.branchId,
            code: form.code,
            name: form.name.trim(),
            departmentId: form.departmentId || null,
            defaultBedClass: form.defaultBedClass,
            nurseStationPhone: form.nurseStationPhone.trim() || null,
          }),
    onSuccess: () => {
      toast.success(ward ? tr('admin', 'ward_updated') : tr('admin', 'ward_created'));
      refresh();
      navigate(returnTo, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const ready = Boolean(form.branchId) && form.code.length >= 2 && form.name.trim().length >= 2;
  const dirty = !save.isSuccess && JSON.stringify(form) !== JSON.stringify(initial);

  return (
    <FormScreen
      title={ward ? tr('admin', 'edit_x', { name: ward.name }) : tr('admin', 'add_a_ward')}
      icon={BedDouble}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (!ready) return;
        setError(null);
        save.mutate();
      }}
    >
      <FormSection title={tr('admin', 'ward')} icon={BedDouble}>
        <Field label={tr('admin', 'branch')} required className="sm:col-span-2">
          <Select
            value={form.branchId}
            disabled={Boolean(ward)}
            readOnly={lock.locked}
            onChange={(e) => setForm({ ...form, branchId: e.target.value, departmentId: '' })}
          >
            <option value="">{tr('admin', 'select_a_branch')}</option>
            {branches.map((branch) => (
              <option key={branch.id} value={branch.id}>
                {branch.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label={tr('admin', 'code')}
          required
          hint={ward ? tr('admin', 'fixed_once_created') : tr('admin', 'short_code_unique_within_the_branch')}
        >
          <Input
            value={form.code}
            disabled={Boolean(ward)}
            maxLength={16}
            onChange={(e) => setForm({ ...form, code: codeEntry(e.target.value) })}
          />
        </Field>
        <Field label={tr('admin', 'name')} required>
          <Input value={form.name} maxLength={160} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </Field>
        {ward ? (
          <Field label={tr('admin', 'department')} hint={tr('admin', 'fixed_once_created')}>
            <Input value={ward.department?.name ?? tr('admin', 'not_set')} disabled readOnly />
          </Field>
        ) : (
          departmentsReadable && (
            <Field label={tr('admin', 'department')} hint={tr('admin', 'the_admitting_department_of_the_ward_optional')}>
              <Select value={form.departmentId} onChange={(e) => setForm({ ...form, departmentId: e.target.value })}>
                <option value="">{tr('admin', 'not_set')}</option>
                {departmentsForBranch.map((department) => (
                  <option key={department.id} value={department.id}>
                    {department.name}
                  </option>
                ))}
              </Select>
            </Field>
          )
        )}
        <Field label={tr('admin', 'default_bed_class')} hint={tr('admin', 'new_beds_in_this_ward_start_with_this_class')}>
          <Select value={form.defaultBedClass} onChange={(e) => setForm({ ...form, defaultBedClass: e.target.value })}>
            {BED_CLASS.map((value) => (
              <option key={value} value={value}>
                {humanize(value, 'bed_class')}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label={tr('admin', 'nurse_station_phone')}
          hint={tr('admin', 'rung_by_the_laboratory_when_a_critical_result')}
          className="sm:col-span-2"
        >
          <Input
            value={form.nurseStationPhone}
            inputMode="tel"
            maxLength={20}
            onChange={(e) => setForm({ ...form, nurseStationPhone: e.target.value })}
          />
        </Field>
      </FormSection>

      {error && <Alert variant="destructive">{error}</Alert>}

      <FormActions
        submitLabel={ward ? tr('admin', 'save_changes') : tr('admin', 'create_ward')}
        loading={save.isPending}
        incomplete={!ready}
        cancelTo={returnTo}
      />
    </FormScreen>
  );
}
